import { StatusChip } from '@/components/core';
import { Card, CardContent } from '@/components/ui-kit/card';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui-kit/tabs';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useProfilePhotoUrl } from '../../hooks/use-profile-photo';
import { employeeName } from '../../schemas/display-name';
import { Employee } from '../../types/employee.types';
import { ContactTab } from './contact-tab';
import { HrInfoTab } from './hr-info-tab';
import { OverviewTab } from './overview-tab';

const DummyProfile = '/assets/images/dummy_profile.png';

export interface EmployeeProfileViewProps {
  employee: Employee;
  /** The employee looking at their own profile (private details are then always shown). */
  isSelf?: boolean;
  /** Buttons shown in the header (edit, change password, ...). */
  actions?: ReactNode;
}

/**
 * Read-only profile of one employee, one tab per record: "Overview" (`UserProfileDetail` and IAM roles),
 * "Contact Info" (`UserContactDetail`, with the personal, academic and previous-employment details) and
 * "HR Info" (every `UserHRPeriodicalProfile`, one expandable box per HR term). The employee directory and
 * "My profile" both show this, since both are the same user setup.
 */
export const EmployeeProfileView = ({
  employee,
  isSelf = false,
  actions,
}: EmployeeProfileViewProps) => {
  const { t } = useTranslation();
  const { data: photoUrl, isLoading: isPhotoLoading } = useProfilePhotoUrl(employee.ProfileImageId);
  const statusChip = (
    <StatusChip tone={employee.IsActive === false ? 'muted' : 'success'} className="w-fit">
      {employee.IsActive === false ? t('INACTIVE') : t('ACTIVE')}
    </StatusChip>
  );

  return (
    <div className="flex w-full flex-col gap-4">
      <Card>
        {/* On a phone (the old portal's mobile profile): the photo, then the name with its status
            chip and the employee ID, centred; the actions as icons in the top-right corner. */}
        <CardContent className="relative flex flex-col gap-4 pt-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-center gap-4 max-sm:flex-col max-sm:gap-3 max-sm:text-center">
            {/* Not the dummy avatar while the real photo's link is on its way. */}
            {isPhotoLoading ? (
              <Skeleton className="size-16 shrink-0 rounded-full max-sm:size-20" />
            ) : (
              <img
                src={photoUrl || DummyProfile}
                alt={employeeName(employee)}
                className="size-16 shrink-0 rounded-full border object-cover max-sm:size-20"
              />
            )}
            <div className="flex min-w-0 flex-col gap-1 max-sm:items-center">
              {/* The chip sits beside the name on a phone, under the employee ID otherwise. */}
              <div className="flex flex-wrap items-center gap-2 max-sm:justify-center">
                <h1 className="break-words text-xl font-semibold max-sm:text-lg">
                  {employeeName(employee)}
                </h1>
                <span className="sm:hidden">{statusChip}</span>
              </div>
              <p className="text-sm text-muted-foreground max-sm:break-all">
                {t('EMPLOYEE_ID')}: {employee.EmployeeID || '-'}
              </p>
              <span className="max-sm:hidden">{statusChip}</span>
            </div>
          </div>

          {actions && (
            <div className="flex flex-wrap items-center gap-2 max-sm:absolute max-sm:right-3 max-sm:top-3">
              {actions}
            </div>
          )}
        </CardContent>
      </Card>

      <Tabs defaultValue="overview">
        {/* Full width on a phone, each tab an equal share (scrolls if they don't fit). */}
        <TabsList className="w-full justify-start overflow-x-auto sm:w-auto">
          <TabsTrigger value="overview" className="max-sm:flex-1">
            {t('OVERVIEW')}
          </TabsTrigger>
          <TabsTrigger value="contact" className="max-sm:flex-1">
            {t('CONTACT_INFO')}
          </TabsTrigger>
          <TabsTrigger value="hr" className="max-sm:flex-1">
            {t('HR_TERMS_INFO')}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="mt-4">
          <OverviewTab employee={employee} />
        </TabsContent>
        <TabsContent value="contact" className="mt-4">
          <ContactTab employee={employee} isSelf={isSelf} />
        </TabsContent>
        <TabsContent value="hr" className="mt-4">
          <HrInfoTab employee={employee} />
        </TabsContent>
      </Tabs>
    </div>
  );
};
